import React from 'react';
import { toast } from '@cognite/cogs.js';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import projectSlice, { ProjectState } from 'redux/reducers/project';
import { AuthState } from 'redux/reducers/auth';
import { ProcedureFileAttachment } from 'types';
import { isFileValid } from '../utils/file';
import { procedureAttachmentsResourceService } from '../resources';
import { useGlobal } from '../redux/reducers/global/hooks';
import { ATTACHMENTS_DATASET_ID } from '../config/apiConfig';

export const useAttachmentFileUpload = () => {
  const dispatch = useDispatch();

  const { getCurrentDataSetId } = useGlobal();

  const { project } = useSelector<RootState, ProjectState>((state) => {
    return state.project;
  });
  const { user } = useSelector<RootState, AuthState>((state) => {
    return state.auth;
  });

  const fileUploadHandler = (file: File) => {
    // add more file validations here if necessary
    if (isFileValid(file)) {
      const reader = new FileReader();
      const attachmentFile: ProcedureFileAttachment = {
        id: `${project.id}_${Date.now()}`,
        name: file.name,
        author: user,
        uploading: true,
        removed: false,
      };
      dispatch(projectSlice.actions.addNewAttachment(attachmentFile));
      reader.readAsArrayBuffer(file);
      reader.onload = async (e) => {
        await procedureAttachmentsResourceService
          .upload(
            `${project.id}`,
            file,
            attachmentFile,
            e.target?.result,
            getCurrentDataSetId(ATTACHMENTS_DATASET_ID)
          )
          .then(() => {
            dispatch(
              projectSlice.actions.setAttachmentToUploaded(attachmentFile)
            );
          })
          .catch(() => {
            dispatch(
              projectSlice.actions.removeFailedAttachment(attachmentFile)
            );
            toast.error(
              <div>
                <h3>Error</h3> We couldn&apos;t upload {attachmentFile.name}.
                <br />
                Try again.
              </div>
            );
          });
      };
    } else {
      toast.warning(
        <div>
          <h3>You can&apos;t attach that</h3> The size limit is 1.2GB, and the
          following types are forbidden: .asp .css .swf .xhtml .rhtml .shtml .js
          .php
        </div>
      );
    }
  };

  const fileSoftDeleteHandler = async (
    fileToDelete: ProcedureFileAttachment
  ) => {
    await procedureAttachmentsResourceService
      .softDelete(fileToDelete)
      .then(() => {
        dispatch(
          projectSlice.actions.updateAttachmentRemovedStatus(fileToDelete)
        );
      })
      .catch(() => {
        toast.error(
          <div>
            <h3>Oops</h3> We couldn’t delete {fileToDelete.name} for some
            reason. Try again.
          </div>
        );
      });
  };

  const undoFileSoftDeleteHandler = async (
    fileToUndoDelete: ProcedureFileAttachment
  ) => {
    await procedureAttachmentsResourceService
      .undoSoftDelete(fileToUndoDelete)
      .then(() => {
        dispatch(
          projectSlice.actions.updateAttachmentRemovedStatus(fileToUndoDelete)
        );
      })
      .catch(() => {
        toast.error(
          <div>
            <h3>Oops</h3> We couldn’t restore {fileToUndoDelete.name}. Try
            again.
          </div>
        );
      });
  };

  return {
    fileUploadHandler,
    fileSoftDeleteHandler,
    undoFileSoftDeleteHandler,
  };
};
